/**
 * CSV export (PLAN §5): `imdbID,Title,Year,WatchedDate,Rating,Rewatch`.
 *
 * Letterboxd's importer accepts either an imdbID or a tmdbID column; this uses
 * imdbID because that is the identifier the catalog now carries whichever
 * source produced it. Films only — Letterboxd does not track television, so a
 * season's id would point at nothing on import; the ExportDialog says so.
 */

import type { Entry, EntryId } from "@schema/catalog.ts";
import type { WatchRecord } from "@schema/persist.ts";
import { toCsv } from "./csv.ts";

/**
 * Letterboxd's own column names and scales, not ours.
 *
 * `Rating` is 0.5-5 in half-star steps — NOT the 1-10 `rating10` the app
 * stores internally. Emitting the internal number under a `Rating10` header
 * produced a file Letterboxd could not read, and that our own importer could
 * not read either, since it looks for `Rating`. See the round-trip test.
 */
const EXPORT_HEADER = ["imdbID", "Title", "Year", "WatchedDate", "Rating", "Rewatch"];

/** 1-10 internal → Letterboxd's 0.5-5 half-star scale. */
function toLetterboxdStars(rating10: number): string {
  return String(rating10 / 2);
}

export function exportLetterboxdCsv(
  entries: Entry[],
  watched: Record<EntryId, WatchRecord>,
): string {
  const byId = new Map(entries.map((e) => [e.id, e]));

  const rows = Object.entries(watched)
    .map(([entryId, record]) => {
      const entry = byId.get(entryId);
      return entry && entry.kind === "movie" ? { entry, record } : null;
    })
    .filter((row): row is { entry: Entry; record: WatchRecord } => row !== null)
    .sort((a, b) => a.entry.orderIndex - b.entry.orderIndex)
    .map(({ entry, record }) => [
      entry.source.imdbId ?? "",
      entry.title,
      String(entry.year),
      record.watchedAt,
      record.rating10 !== null ? toLetterboxdStars(record.rating10) : "",
      record.rewatch ? "true" : "false",
    ]);

  return toCsv(EXPORT_HEADER, rows);
}
